import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormHeader } from './FormHeader';
import { FormError } from './FormError';
import { SubmitButton } from './SubmitButton';
import { PasswordField } from './PasswordField';
import { useLoginForm } from '../hooks/useLoginForm';

/*
 * No `required` asterisks on the auth surfaces — a deliberate design choice for
 * the signed-out experience, NOT a general "all fields required" rule.
 * Everywhere inside the application, required fields DO carry the asterisk,
 * whether or not the form also has optional fields.
 */
export function LoginForm() {
  const { t } = useTranslation();
  const { form, onSubmit, isLoading, error, goToForgotPassword } = useLoginForm();
  const [tenantCode, email, password] = useWatch({
    control: form.control,
    name: ['tenant_code', 'email', 'password'],
  });
  const isFormPopulated = Boolean(tenantCode && email && password);

  return (
    <div className="w-full" aria-busy={isLoading || undefined}>
      <FormHeader title={t('auth.signInPrompt')} />

      <FormError message={error} />

      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <FormField
            control={form.control}
            name="tenant_code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.tenantCode')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('auth.tenantCodePlaceholder')}
                    autoComplete="organization"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.emailAddress')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t('auth.emailPlaceholder')}
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.password')}</FormLabel>
                <FormControl>
                  <PasswordField
                    placeholder="••••••••"
                    autoComplete="current-password"
                    toggleLabel={t('auth.togglePasswordVisibility')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Controller
            control={form.control}
            name="remember_me"
            render={({ field }) => (
              <label className="flex select-none items-center gap-2.5 pt-1 text-sm text-muted-foreground">
                <Checkbox
                  name="remember_me"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                {t('auth.rememberMe')}
              </label>
            )}
          />

          <SubmitButton
            loading={isLoading}
            loadingLabel={t('common.loading')}
            disabled={!isFormPopulated}
          >
            {t('auth.signIn')}
          </SubmitButton>

          <div className="text-center">
            <button
              type="button"
              onClick={goToForgotPassword}
              className="rounded-sm text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
            >
              {t('auth.forgotPassword')}
            </button>
          </div>

          {/*
            TODO(#561): "Sign in with Microsoft" (Entra SSO) button belongs here —
            an "or" divider + a Microsoft OAuth button. Hidden until SSO ships.
          */}
        </form>
      </Form>
    </div>
  );
}
